/**
 * The twelve cells of the Dharshana.
 *
 * Source of truth: /app/tmp/greengold/GreenGold_Website_Build_Package/reference/dharshana_content_map_v2.md
 *
 * Columns:   green · gold · grid
 * Rows:      individual → community → institutional → civilisational (self → civilisation)
 *
 * Every cell resolves to a URL slug `${col}-${row}` and exposes:
 *   - short title + one-line promise (used by the grid tiles)
 *   - narrative (2–3 sentences on the app-page)
 *   - triggers (KB slugs for <HoverChat>)
 *   - actionRoute + actionLabel (exactly one inline <Action> per cell)
 *
 * ≥250 kWh reconciliation is baked into every Individual / Community narrative:
 * they speak of co-owning a share of a larger community asset — never a home
 * battery sale.
 *
 * CTA route mapping — 10 of the 12 land on canonical Phase 1 routes.
 * Two entries route to book-tour and partnership-rfp because the content map
 * introduces two audiences (community-conveners and standards/policy partners)
 * for which no dedicated overlay was defined in 04_COMPONENT_AND_TECH_SPEC §3.2:
 *   - "Start a community project / cooperative"      → book-tour  (needs a conversation)
 *   - "Co-architect the protocol"                    → partnership-rfp
 * See PRD.md §Phase-2 spec-drift for the note to the client.
 */

export const COLUMNS = [
  { id: "green", label: "GREEN", accent: "var(--green-bright)", subtitle: "make & store" },
  { id: "gold", label: "GOLD", accent: "var(--gold)", subtitle: "own & prosper" },
  { id: "grid", label: "GRID", accent: "#7CC9FF", subtitle: "intelligence" },
];

export const ROWS = [
  { id: "individual", label: "Individual" },
  { id: "community", label: "Community" },
  { id: "institutional", label: "Institutional" },
  { id: "civilisational", label: "Civilisational" },
];

// Ordered row-major so a mobile scroll reads Individual → Civilisational
// naturally, per 02_SITEMAP_AND_NAVIGATION.md §"12-cell grid summary".
export const CELLS = [
  // ---------- GREEN ----------
  {
    slug: "green-individual",
    col: "green",
    row: "individual",
    title: "A share of the machine that powers your street.",
    promise: "Personal sovereignty as a titled slice of a community asset.",
    narrative:
      "Sovereignty begins as a personal fact: knowing the power under your roof answers to you. For a household or shopkeeper, that ownership is real not as a lone battery but as a titled slice of a community storage asset — modular, upgradeable, built for a 15-year life.",
    triggers: ["energy-sovereignty"],
    audience: "Prosumer · Citizen",
    action: {
      route: "reserve-block",
      pre: "You can ",
      linkText: "reserve a Sovereign Block in a project near you",
      post: ".",
    },
  },
  {
    slug: "green-community",
    col: "green",
    row: "community",
    title: "One system, many owners.",
    promise: "The ≥250 kWh unit that actually gets built.",
    narrative:
      "A single BESS serves a housing colony, a village, or an industrial cluster, its economics split fairly across everyone it powers. Pooled ownership turns a shared cost into a shared asset — this is the ≥250 kWh unit that actually gets built.",
    triggers: ["fractional-ownership"],
    audience: "Community leader · RWA",
    action: {
      route: "book-tour",
      queryParams: { visit_type: "community-cooperative" },
      pre: "If you can convene the households, we can build the asset — ",
      linkText: "start a community project",
      post: ".",
    },
  },
  {
    slug: "green-institutional",
    col: "green",
    row: "institutional",
    title: "Storage at the scale industry actually runs on.",
    promise: "Containerised SB / BO for DISCOMs, hospitals, campuses, C&I.",
    narrative:
      "Containerised, liquid-cooled grid-scale BESS for DISCOMs, hospitals, campuses and C&I estates — solar-plus-storage (SB) or storage-only (BO) for those who already generate. Minimum 250 kWh, no upper limit, price-competitive on imported cells today and our own cells tomorrow.",
    triggers: ["lfp", "bms"],
    audience: "Customer · ≥250 kWh",
    action: {
      route: "sovereign-audit",
      pre: "Tell us your load and tariff and we'll size it — ",
      linkText: "run a Sovereign Audit and send us your RFP",
      post: ".",
    },
  },
  {
    slug: "green-civilisational",
    col: "green",
    row: "civilisational",
    title: "From coconut shell to cell — a sovereign supply chain.",
    promise: "720 MWh/yr. LFP now. Sodium-ion next. Same line.",
    narrative:
      "The Tamil Nadu gigafactory: 720 MWh a year, LFP now and sodium-ion next on the same machinery, its hard-carbon anode grown from Tamil Nadu's own coconut shells. Not an assembly line for imported materials — an integrated, non-Chinese cell-to-pack chain for the Indo-Pacific.",
    triggers: ["sodium-ion", "coconut-shell-hard-carbon", "boot-spv"],
    audience: "Funder · Strategic",
    action: {
      route: "request-data-room",
      pre: "This is the asset being allocated to a single co-owning partner — ",
      linkText: "request access to the SPV data room",
      post: ".",
    },
  },
  // ---------- GOLD ----------
  {
    slug: "gold-individual",
    col: "gold",
    row: "individual",
    title: "Your power bill, running in reverse.",
    promise: "Bill credits · carbon-credit share · Sovereignty Score.",
    narrative:
      "Ownership pays you back in plain money: bill credits, a share of monetised carbon credits, and a Sovereignty Score that a bank can price and a buyer can trust. The energy under your roof becomes an asset on your balance sheet, not a line on your expenses.",
    triggers: ["sovereignty-score", "carbon-credits", "energy-passport"],
    audience: "Prosumer · Citizen",
    action: {
      route: "reserve-block",
      pre: "See what a block would actually return to you, then ",
      linkText: "reserve one",
      post: ".",
    },
  },
  {
    slug: "gold-community",
    col: "gold",
    row: "community",
    title: "The AAVIN pattern, for power.",
    promise: "Households as one legal consumer. Local jobs.",
    narrative:
      "The same cooperative shape that turned Indian dairying from deficit into the world's largest — AAVIN, the Anand pattern — applied to energy. Households aggregate as one legal consumer, community carbon credits are pooled and monetised, and local technicians earn neighbourhood livelihoods servicing it all.",
    triggers: ["aavin"],
    audience: "Cooperative · Community",
    action: {
      route: "book-tour",
      queryParams: { visit_type: "community-cooperative" },
      pre: "We'll help you form it end to end — ",
      linkText: "start a cooperative",
      post: ".",
    },
  },
  {
    slug: "gold-institutional",
    col: "gold",
    row: "institutional",
    title: "Buy the power plant, not just the power.",
    promise: "GCPP: ₹8–12 → ₹4–5.50/kWh. CBAM-grade traceable.",
    narrative:
      "Under the Group Captive framework, an industry holding ≥26% equity and consuming ≥51% of the output escapes cross-subsidy and watches landed cost fall from ₹8–12 to ₹4–5.50/kWh — with CBAM-grade traceable green power built in for exporters.",
    triggers: ["gcpp", "cbam", "open-access"],
    audience: "Industry · Exporter",
    action: {
      route: "gcpp-model",
      pre: "Put in your numbers and see the captive savings, then ",
      linkText: "send us an RFP",
      post: ".",
    },
  },
  {
    slug: "gold-civilisational",
    col: "gold",
    row: "civilisational",
    title: "The network, not the factory, is the asset.",
    promise: "Urban mining · second-life · geography-by-geography licensing.",
    narrative:
      "At end-of-life a battery is not waste but a deposit of recoverable lithium, iron and aluminium — urban mining and second-life deployment close the loop and lower the cost of sovereignty every cycle. The model itself is licensed geography to geography: local owners, local jobs, one shared standard.",
    triggers: ["urban-mining", "global-licensing"],
    audience: "Funder · Strategic",
    action: {
      route: "request-data-room",
      pre: "Co-own not a bet but a beginning — ",
      linkText: "explore a partnership allocation",
      post: ".",
    },
  },
  // ---------- GRID ----------
  {
    slug: "grid-individual",
    col: "grid",
    row: "individual",
    title: "An energy brain that works while you don't think about it.",
    promise: "Personal energy AI · BMS · digital twin.",
    narrative:
      "A personal energy AI learns your usage, maximises solar self-consumption, and quietly arbitrages tariffs — every cell watched in real time by our BMS and its digital twin. Sovereignty you never have to operate.",
    triggers: ["bms", "digital-twin"],
    audience: "Prosumer · Curious",
    action: {
      route: "reserve-block",
      pre: "Ask it anything about your own numbers, or ",
      linkText: "reserve a block to get one",
      post: ".",
    },
  },
  {
    slug: "grid-community",
    col: "grid",
    row: "community",
    title: "Sell your surplus to your neighbour.",
    promise: "Peer-to-peer trading · virtual net-metering.",
    narrative:
      "Peer-to-peer trading lets power flow from a rooftop with a surplus to a home that needs it, at a fair negotiated price, governed by a transparent protocol and virtual net-metering. A street becomes its own small market.",
    triggers: ["peer-to-peer-trading", "virtual-net-metering"],
    audience: "Community leader",
    action: {
      route: "book-tour",
      queryParams: { visit_type: "community-cooperative" },
      pre: "Want your colony to trade its own power? ",
      linkText: "start a community project",
      post: ".",
    },
  },
  {
    slug: "grid-institutional",
    col: "grid",
    row: "institutional",
    title: "One screen for the whole grid.",
    promise: "griddata · VPP · RDSS-aligned. Proved in twelve months.",
    narrative:
      "griddata makes every panel, battery, meter and substation visible and dispatchable in real time — running the virtual power plant, settling Group Captive and cooperative ownership to the fraction, and mapping directly onto RDSS. Proved on one substation in twelve months, then licensed state to state.",
    triggers: ["griddata", "vpp", "rdss"],
    audience: "DISCOM · Institution",
    action: {
      route: "book-tour",
      pre: "Start with one substation — ",
      linkText: "open a griddata pilot",
      post: ".",
    },
  },
  {
    slug: "grid-civilisational",
    col: "grid",
    row: "civilisational",
    title: "An open protocol any grid on Earth can join.",
    promise: "The commons, not a company.",
    narrative:
      "The endpoint is not a company but a commons: an open energy-sovereignty standard that any battery, any inverter, any DISCOM can join — the architecture of the internet applied to energy, so that dependence becomes the anomaly, not the norm.",
    triggers: ["open-protocol"],
    audience: "Policy · Standards",
    action: {
      route: "partnership-rfp",
      pre: "Help write the standard rather than inherit one — ",
      linkText: "co-architect the protocol",
      post: ".",
    },
  },
];

export const CELL_BY_SLUG = Object.fromEntries(CELLS.map((c) => [c.slug, c]));

export function cellAt(colId, rowId) {
  return CELLS.find((c) => c.col === colId && c.row === rowId);
}
